/**
 * Стартовое предложение набора тем (#845, PR 4; спека §2 Р1) — блок в панели
 * набора, только для тех, кто набор правит (client, admin).
 *
 * - Набор пуст и предложения нет — «Предложить темы по истории» (по `topic` и
 *   категории разговоров за 90 дней). Истории нет (или поле анализа «тема»
 *   выключено) — экран так и говорит и ведёт к ручному вводу ниже.
 * - Предложение готовится — «готовится», состояние перечитывается само.
 * - Готово — экран утверждения (`VoiceThemeBootstrapProposal`) и «Предложить
 *   заново», доступное не чаще раза в сутки (до того — со временем, когда
 *   станет доступно).
 * - Сбой — причина и повтор, доступный через час.
 * - Брошенная попытка (`running` старше окна перезахвата, `stale` от сервера) —
 *   не ждём, предлагаем кнопку.
 * - Нечего отправлять (всё удержано редакцией) — история читается уже внутри
 *   генерации, поэтому попытка закрывается сбоем `no_history`, а сервер отдаёт
 *   это как `notice` состояния: экран говорит об этом, с отсылкой к ручному
 *   вводу, и не называет это сбоем.
 * - Сохранённый результат не читается (`unreadable`) — это не «уже утверждено»:
 *   экран просит предложить заново, кнопка доступна сразу.
 * - Итог утверждения («добавлено N, не поместилось M») и отказ утверждения
 *   держит этот блок: сам экран утверждения после перечитывания состояния
 *   размонтируется или сменяется новым списком (`bootstrap_changed`).
 * - Блок виден, пока есть что сказать, и при непустом наборе: сбой или брошенная
 *   попытка «Предложить заново», сообщение «нечего отправлять», готовое
 *   неутверждённое предложение, итог или отказ утверждения. Молча исчезнуть он
 *   может, только когда сказать нечего. Новость о прошлой попытке (сбой,
 *   брошенная попытка, «нечего отправлять») над непустым набором живёт сутки
 *   после закрытия попытки и убирается кнопкой «Закрыть»; над пустым набором она
 *   остаётся — блок там и есть способ начать.
 * - «Предложить заново» не размонтирует экран утверждения: пока запрос идёт,
 *   правки остаются на месте (поля недоступны), а отказ (429, не захвачено)
 *   оставляет их как были. Список меняется, только когда сервер действительно
 *   отдал другое предложение.
 *
 * Загрузка экрана ничего не запускает: генерация — только кнопками.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import {
  THEME_BOOTSTRAP_UNREADABLE_CODE,
  type ApproveThemeBootstrapResponse,
  type ThemeBootstrapState,
} from '@aica/api-types/schemas';
import { Button } from '@/components/ui/button';
import { formatDateTimeLocaleShort } from '@/lib/utils';
import { bootstrapErrorMessage } from './bootstrap-error-message';
import { useGenerateThemeBootstrap, useThemeBootstrap } from './useThemeBootstrap';
import { VoiceThemeBootstrapProposal } from './VoiceThemeBootstrapProposal';

interface VoiceThemeBootstrapProps {
  /** No theme in use — the place to start from history. */
  setEmpty: boolean;
}

/**
 * How long news about a past attempt (failure, abandoned attempt, «nothing to
 * send») stays over a set that already has themes: a day after it closed.
 */
const THEME_BOOTSTRAP_NEWS_TTL_MS = 24 * 60 * 60 * 1000;

/** The moment has not come yet (a press would be refused by the claim). */
const notYet = (iso: string | null): boolean =>
  iso !== null && Date.parse(iso) > Date.now();

/**
 * Re-render when `iso` comes: `notYet` is evaluated on render only, and a
 * screen left open while nothing else changes would keep a button disabled past
 * the moment the server accepts the press.
 */
function useRerenderAt(iso: string | null): void {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (iso === null) return;
    const delay = Date.parse(iso) - Date.now();
    if (!(delay > 0)) return;
    const timer = setTimeout(() => setTick((tick) => tick + 1), delay + 1);
    return () => clearTimeout(timer);
  }, [iso]);
}

export function VoiceThemeBootstrap({ setEmpty }: VoiceThemeBootstrapProps) {
  const { t } = useTranslation(['dashboard', 'records']);
  const query = useThemeBootstrap({ enabled: true });
  useRerenderAt(query.data?.nextAttemptAt ?? null);
  const generate = useGenerateThemeBootstrap();
  const [error, setError] = useState<string | null>(null);
  // Why an approval was refused, kept here for the same reason as the outcome.
  const [approveError, setApproveError] = useState<string | null>(null);
  // The outcome of an approval, kept until dismissed (see the header).
  const [approved, setApproved] = useState<ApproveThemeBootstrapResponse | null>(null);
  // The attempt (its claim token) whose failure news the admin dismissed; a new
  // attempt that fails again is news again.
  const [dismissedAttempt, setDismissedAttempt] = useState<string | null>(null);

  const run = async (mode: 'start' | 'regenerate') => {
    setError(null);
    setApproveError(null);
    try {
      await generate.mutateAsync(mode);
    } catch (failure) {
      setError(await bootstrapErrorMessage(failure, t));
    }
  };

  const state: ThemeBootstrapState | undefined = query.data;
  const pendingProposal =
    state?.status === 'ready' && state.proposal !== null && state.approvedAt === null;
  // An attempt that is really being prepared (an abandoned one is not).
  const preparing = state?.status === 'running' && !state.stale;
  const failed = state?.status === 'failed';
  // With themes in the set, news about a past attempt (a failure, an abandoned
  // attempt, the notice of a press that found nothing to send) is shown only
  // while it is recent and until the admin dismisses it — otherwise a failure
  // of a «suggest again» from months ago would sit over a working set for good.
  // An empty set keeps showing it: the block is the way to start there.
  const attemptMs = Date.parse(state?.finishedAt ?? state?.requestedAt ?? '');
  // The news expires by itself: `hushed` is evaluated on render only, and a
  // screen left open with nothing else changing would show it past its day.
  useRerenderAt(
    !setEmpty && Number.isFinite(attemptMs)
      ? new Date(attemptMs + THEME_BOOTSTRAP_NEWS_TTL_MS).toISOString()
      : null
  );
  const hushed =
    !setEmpty &&
    ((Number.isFinite(attemptMs) &&
      Date.now() - attemptMs > THEME_BOOTSTRAP_NEWS_TTL_MS) ||
      (state?.requestedAt != null && dismissedAttempt === state.requestedAt));
  const notice = hushed ? null : (state?.notice ?? null);
  const failedNews = failed && !hushed;
  const staleNews = state?.stale === true && !hushed;
  // With themes in the set the block is shown only when it has something to
  // say: a failure or an abandoned attempt (of a «suggest again»), the notice
  // of a press that found nothing to send, a proposal waiting for approval, an
  // outcome or a refusal of an approval.
  const somethingToSay =
    setEmpty ||
    pendingProposal ||
    preparing ||
    failedNews ||
    staleNews ||
    notice !== null ||
    approved !== null ||
    approveError !== null ||
    error !== null;
  if (!somethingToSay) return null;

  const regenerateButton = state ? (
    <Button
      variant="outline"
      size="sm"
      disabled={generate.isPending || notYet(state.nextAttemptAt)}
      onClick={() => void run('regenerate')}
    >
      {notYet(state.nextAttemptAt)
        ? t('voicePage.themes.bootstrap.regenerateAt', {
            time: formatDateTimeLocaleShort(state.nextAttemptAt),
          })
        : t('voicePage.themes.bootstrap.regenerate')}
    </Button>
  ) : null;

  const countOf = (status: 'created' | 'duplicate' | 'set_full'): number =>
    approved?.results.filter((r) => r.status === status).length ?? 0;
  // A name held by an archived theme: nothing was created or restored.
  const archivedNames =
    approved?.results.filter((r) => r.status === 'archived').map((r) => r.name) ?? [];

  const body = (() => {
    if (approved) {
      return (
        <div className="space-y-2">
          <p role="status" className="text-sm text-fg">
            {t('voicePage.themes.bootstrap.approved', {
              created: countOf('created'),
              duplicate: countOf('duplicate'),
              full: countOf('set_full'),
            })}
          </p>
          {archivedNames.length > 0 && (
            <p role="status" className="text-sm text-fg">
              {t('voicePage.themes.bootstrap.approvedArchived', {
                names: archivedNames.join(', '),
              })}
            </p>
          )}
          <Button variant="outline" size="sm" onClick={() => setApproved(null)}>
            {t('voicePage.themes.bootstrap.dismiss')}
          </Button>
        </div>
      );
    }
    if (query.isPending) {
      return (
        <p className="text-sm text-fg-muted" role="status">
          {t('voicePage.themes.bootstrap.loading')}
        </p>
      );
    }
    if (!state) {
      return (
        <div className="space-y-2" role="alert">
          <p className="text-sm text-danger">
            {t('voicePage.themes.bootstrap.loadFailed')}
          </p>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t('voicePage.themes.retry')}
          </Button>
        </div>
      );
    }
    if (pendingProposal && state.proposal && state.requestedAt) {
      return (
        <div className="space-y-3">
          {generate.isPending && (
            <p className="text-sm text-fg-muted" role="status">
              {t('voicePage.themes.bootstrap.running')}
            </p>
          )}
          <VoiceThemeBootstrapProposal
            // A new proposal starts a fresh edit; a refused «suggest again»
            // answers the same one, and the drafts stay.
            key={state.requestedAt}
            proposal={state.proposal}
            requestedAt={state.requestedAt}
            regenerate={regenerateButton}
            disabled={generate.isPending}
            onApproveError={setApproveError}
            onApproved={(response) => {
              setApproveError(null);
              setApproved(response);
            }}
          />
        </div>
      );
    }
    if (preparing || generate.isPending) {
      return (
        <p className="text-sm text-fg-muted" role="status">
          {t('voicePage.themes.bootstrap.running')}
        </p>
      );
    }
    if (state.status === 'ready') {
      // Approved before, and the set is empty again (a result that does not
      // read comes as a failure, below).
      return (
        <div className="space-y-2">
          <p className="text-sm text-fg-muted">
            {t('voicePage.themes.bootstrap.approvedBefore')}
          </p>
          {regenerateButton}
        </div>
      );
    }
    // «No history — enter the themes manually» is the onboarding of an EMPTY
    // set. Over a set that has themes the news below (failure, notice) is shown
    // instead, with its Dismiss, and the manual-entry text never.
    if (setEmpty && state.historyCalls === 0) {
      return (
        <p className="text-sm text-fg-muted">
          {t('voicePage.themes.bootstrap.noHistory')}
        </p>
      );
    }
    const waiting = failed && notYet(state.nextAttemptAt);
    return (
      <div className="space-y-2">
        {/* The intro is for starting an empty set. */}
        {setEmpty && (
          <p className="text-sm text-fg-muted">
            {state.historyCalls === null
              ? t('voicePage.themes.bootstrap.intro')
              : t('voicePage.themes.bootstrap.introWithHistory', {
                  calls: state.historyCalls,
                })}
          </p>
        )}
        {staleNews && (
          <p className="text-sm text-fg-muted">
            {t('voicePage.themes.bootstrap.abandoned')}
          </p>
        )}
        {notice && (
          <p role="status" className="text-sm text-fg">
            {t('voicePage.themes.bootstrap.noSendableHistory')}
            {notice.withheld > 0 &&
              ` ${t('voicePage.themes.bootstrap.noSendableHistoryWithheld', {
                withheld: notice.withheld,
              })}`}
          </p>
        )}
        {failedNews && state.errorCode !== 'no_history' && (
          <p className="text-sm text-danger">
            {state.errorCode === 'parse'
              ? t('voicePage.themes.bootstrap.failedParse')
              : state.errorCode === 'no_themes'
                ? t('voicePage.themes.bootstrap.failedNoThemes')
                : state.errorCode === THEME_BOOTSTRAP_UNREADABLE_CODE
                  ? t('voicePage.themes.bootstrap.failedUnreadable')
                  : t('voicePage.themes.bootstrap.failed')}
          </p>
        )}
        <Button
          size="sm"
          disabled={generate.isPending || waiting}
          onClick={() => void run('start')}
        >
          <Sparkles className="mr-2 h-4 w-4" aria-hidden="true" />
          {waiting
            ? t('voicePage.themes.bootstrap.retryAt', {
                time: formatDateTimeLocaleShort(state.nextAttemptAt),
              })
            : t('voicePage.themes.bootstrap.start')}
        </Button>
        {!setEmpty &&
          (failedNews || staleNews || notice !== null) &&
          state.requestedAt && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDismissedAttempt(state.requestedAt)}
            >
              {t('voicePage.themes.bootstrap.dismiss')}
            </Button>
          )}
      </div>
    );
  })();

  return (
    <section
      className="space-y-3 rounded-card border border-border bg-bg-surface px-4 py-4"
      aria-labelledby="voice-themes-bootstrap"
    >
      <h3 id="voice-themes-bootstrap" className="text-sm font-semibold text-fg">
        {t('voicePage.themes.bootstrap.title')}
      </h3>
      {body}
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      {approveError && (
        <div role="alert" className="flex flex-wrap items-center gap-2">
          <p className="text-sm text-danger">{approveError}</p>
          <Button variant="outline" size="sm" onClick={() => setApproveError(null)}>
            {t('voicePage.themes.bootstrap.dismiss')}
          </Button>
        </div>
      )}
    </section>
  );
}
